/**
 * Shared hook for fetching ObjectRecord picker options.
 *
 * Used by both ObjectReferenceField (single-select) and
 * ObjectReferenceMultiField (multi-select). Calls MDM's existing
 * list endpoint directly with search, pagination, and include_archived=false.
 */

import {useState, useEffect, useMemo} from 'react';
import {useObjectRecords} from '@/matic/features/master-data/hooks/useObjectRecords';
import type {ObjectRecord} from '@/matic/features/master-data/types/master-data-types';
import type {PickerOption} from './types';

/**
 * Encapsulates data fetching and 300ms search debouncing for object reference pickers.
 *
 * @param refTypeId - UUID of the target ObjectType
 * @param searchTerm - Current search text (debounced internally)
 * @returns Picker options and loading state
 */
export function useObjectRecordPicker(refTypeId: string, searchTerm: string) {
  const [debouncedTerm, setDebouncedTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const {data: recordsData, isLoading} = useObjectRecords(refTypeId, {
    search: debouncedTerm || undefined,
    limit: 100,
  });

  const options: PickerOption[] = useMemo(
    () =>
      (recordsData?.items ?? []).map((record: ObjectRecord) => ({
        id: record.id,
        code: record.code,
        detail: record.display ?? undefined,
      })),
    [recordsData]
  );

  return {options, isLoading: Boolean(refTypeId) && isLoading};
}
